import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';


const SearchBar = ({ onSearch, onFilterToggle, isFilterOpen }) => {
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const mockSuggestions = [
    { type: 'tracking', value: '1Z999AA10123456784', carrier: 'UPS' },
    { type: 'tracking', value: '1Z999AA10123456785', carrier: 'UPS' },
    { type: 'tracking', value: '9400111899223344556677', carrier: 'USPS' },
    { type: 'tracking', value: '420221559400111899223344556677', carrier: 'USPS' },
    { type: 'tracking', value: '123456789012', carrier: 'FedEx' },
    { type: 'tracking', value: '987654321098', carrier: 'FedEx' },
    { type: 'recent', value: 'Last 7 days shipments', icon: 'Clock' },
    { type: 'recent', value: 'Today\'s entries', icon: 'Calendar' },
    { type: 'carrier', value: 'All UPS shipments', icon: 'Package' },
    { type: 'carrier', value: 'All FedEx shipments', icon: 'Package' }
  ];

  useEffect(() => {
    if (searchValue?.length >= 2) {
      const filtered = mockSuggestions?.filter(item =>
        item?.value?.toLowerCase()?.includes(searchValue?.toLowerCase())
      );
      setSuggestions(filtered?.slice(0, 8));
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [searchValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef?.current &&
        !suggestionsRef?.current?.contains(event?.target) &&
        !inputRef?.current?.contains(event?.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions?.length === 0) return;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e?.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions?.[selectedIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion?.value);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSearch(suggestion?.value);
  };

  const handleSearch = () => {
    if (searchValue?.trim()) {
      onSearch(searchValue);
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    onSearch('');
    inputRef?.current?.focus();
  };

  const getSuggestionIcon = (suggestion) => {
    if (suggestion?.type === 'tracking') return 'Hash';
    if (suggestion?.icon) return suggestion?.icon;
    return 'Search';
  };

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-12 w-full">
        <div className="relative flex-1" ref={inputRef}>
          <div className="absolute left-18 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <Icon 
              name="Search" 
              size={20} 
              color="var(--color-muted-foreground)" 
              strokeWidth={2} 
            />
          </div>
          
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e?.target?.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by tracking number, carrier, or date..."
            className="w-full h-[56px] pl-[52px] pr-[120px] rounded-lg border border-border bg-card text-foreground font-body text-[15px] focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
            aria-label="Search shipment history"
            aria-autocomplete="list"
            aria-controls="search-suggestions"
            aria-expanded={showSuggestions}
          />

          {searchValue && (
            <button
              onClick={handleClear}
              className="absolute right-[72px] top-1/2 -translate-y-1/2 w-[32px] h-[32px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
              aria-label="Clear search"
            >
              <Icon 
                name="X" 
                size={16} 
                color="var(--color-muted-foreground)" 
                strokeWidth={2} 
              />
            </button>
          )}

          <button
            onClick={handleSearch}
            className="absolute right-12 top-1/2 -translate-y-1/2 w-[48px] h-[40px] bg-primary text-primary-foreground rounded-lg flex items-center justify-center hover:bg-primary/90 transition-smooth"
            aria-label="Execute search"
          >
            <Icon 
              name="ArrowRight" 
              size={20} 
              color="currentColor" 
              strokeWidth={2} 
            />
          </button>

          {showSuggestions && suggestions?.length > 0 && (
            <div
              ref={suggestionsRef}
              id="search-suggestions"
              className="absolute top-full left-0 right-0 mt-6 bg-card border border-border rounded-lg shadow-elevation-3 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200"
              role="listbox"
            >
              {suggestions?.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`
                    w-full flex items-center gap-12 px-18 py-12 text-left
                    transition-smooth hover:bg-muted
                    ${selectedIndex === index ? 'bg-muted' : ''}
                  `}
                  role="option"
                  aria-selected={selectedIndex === index}
                >
                  <Icon 
                    name={getSuggestionIcon(suggestion)} 
                    size={18} 
                    color="var(--color-muted-foreground)" 
                    strokeWidth={2} 
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-[14px] text-foreground truncate">
                      {suggestion?.value}
                    </div>
                    {suggestion?.carrier && (
                      <div className="font-caption text-[12px] text-muted-foreground">
                        {suggestion?.carrier}
                      </div>
                    )}
                  </div>
                  {suggestion?.type === 'tracking' && (
                    <div className="flex-shrink-0 px-8 py-2 bg-primary/10 rounded text-primary font-caption text-[11px] font-medium">
                      {suggestion?.carrier}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={onFilterToggle}
          className={`
            flex-shrink-0 w-[56px] h-[56px] rounded-lg border transition-smooth
            flex items-center justify-center
            ${isFilterOpen 
              ? 'bg-primary text-primary-foreground border-primary' 
              : 'bg-card text-foreground border-border hover:bg-muted'
            }
          `}
          aria-label={isFilterOpen ? 'Close filters' : 'Open filters'}
          aria-expanded={isFilterOpen}
        >
          <Icon 
            name="SlidersHorizontal" 
            size={22} 
            color="currentColor" 
            strokeWidth={2} 
          />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;